import { __assign, __awaiter, __generator } from "tslib";
import classNames from "classnames";
import React, { isValidElement, useEffect, useMemo, useState } from "react";
import { CloseIcon, MenuIcon } from "../Icon/Icon";
import { Logo } from "../Logo/Logo";
import { useVSETHContext } from "../VSETHContext/VSETHContext";
import { ActionItem } from "./ActionItem";
import { globalNav } from "./GlobalNav";
import { ItemComponent } from "./ItemComponent";

function isLangMap(obj) {
  return obj.hasOwnProperty("en");
}

function getTitle(title, lang) {
  var _a;

  if (title === undefined || title === null) return undefined;
  if (typeof title === "string" || typeof title === "number" || typeof title === "boolean" || Array.isArray(title)) return title;

  if (isValidElement(title)) {
    return title;
  }

  if (isLangMap(title)) {
    return (_a = title[lang]) !== null && _a !== void 0 ? _a : title.en;
  }

  return undefined;
}
/**
 *
 * @param items List of Items
 * @param lang language code (eg. "en" or "de")
 *
 * Takes an Item array and returns an Item array where every title is in the target language if possible, defaults to "en" if unavailable
 */


function translate(items, lang) {
  return items.map(function (item) {
    return __assign(__assign({}, item), {
      title: getTitle(item.title, lang),
      childItems: item.childItems ? translate(item.childItems, lang) : item.childItems
    });
  });
}

export var VSETHNavbar = function (_a) {
  var primaryLogo = _a.primaryLogo,
      _b = _a.primarySignet,
      primarySignet = _b === void 0 ? React.createElement(Logo, {
    height: 32,
    variant: "signet"
  }) : _b,
      primaryItems = _a.primaryItems,
      _c = _a.primaryActionItems,
      primaryActionItems = _c === void 0 ? [] : _c,
      _d = _a.primarySlot,
      primarySlot = _d === void 0 ? null : _d,
      _e = _a.secondarySlot,
      secondarySlot = _e === void 0 ? null : _e,
      _f = _a.mobileTopSlot,
      mobileTopSlot = _f === void 0 ? null : _f,
      _g = _a.drawerSlot,
      drawerSlot = _g === void 0 ? null : _g,
      _h = _a.secondaryNavItems,
      secondaryNavItems = _h === void 0 ? [] : _h,
      _j = _a.secondaryLogo,
      secondaryLogo = _j === void 0 ? null : _j,
      _k = _a.secondarySignet,
      secondarySignet = _k === void 0 ? null : _k,
      _l = _a.lang,
      lang = _l === void 0 ? "en" : _l,
      NavLink = _a.NavLink;

  var _m = useVSETHContext(),
      globID = _m.globID,
      static_url = _m.static_url;

  var navbarURL = static_url + "/assets/" + globID + "/navbar-extern.json";
  var navbarConfigKey = "@vseth/navbar_config-" + globID;

  var _o = useState(false),
      expanded = _o[0],
      setExpanded = _o[1]; // Get saved config from store, if unavailable, default to GlobalNav.ts


  var storedPrimaryItems = useMemo(function () {
    return typeof window !== "undefined" ? localStorage.getItem(navbarConfigKey) : undefined;
  }, [navbarConfigKey]);

  var _p = useState(storedPrimaryItems ? JSON.parse(storedPrimaryItems) : globalNav),
      externalPrimaryItems = _p[0],
      setExternalPrimaryItems = _p[1]; // Try to load new Navbar config, save and replace it with new one if it changed


  useEffect(function () {
    var cancel = false;

    function fetchData() {
      return __awaiter(this, void 0, void 0, function () {
        var response, navConfig, stringified;
        return __generator(this, function (_a) {
          switch (_a.label) {
            case 0:
              if (!(typeof window !== "undefined")) return [3
              /*break*/
              , 3];
              return [4
              /*yield*/
              , fetch(navbarURL)];

            case 1:
              response = _a.sent();
              if (cancel) return [2
              /*return*/
              ];
              return [4
              /*yield*/
              , response.json()];

            case 2:
              navConfig = _a.sent();
              if (cancel) return [2
              /*return*/
              ];
              stringified = JSON.stringify(navConfig);

              if (stringified !== storedPrimaryItems) {
                localStorage.setItem(navbarConfigKey, stringified);
                setExternalPrimaryItems(navConfig);
              }

              _a.label = 3;

            case 3:
              return [2
              /*return*/
              ];
          }
        });
      });
    }

    fetchData();
    return function () {
      cancel = true;
    };
  }, [navbarConfigKey, navbarURL]); // Need to process all objects as title values to the localised string

  var processedPrimaryItems = useMemo(function () {
    return translate(primaryItems !== null && primaryItems !== void 0 ? primaryItems : externalPrimaryItems, lang);
  }, [lang, primaryItems, externalPrimaryItems]);
  var primaryItemsComponent = useMemo(function () {
    return processedPrimaryItems.map(function (item, i) {
      return React.createElement(ItemComponent, {
        item: __assign(__assign({}, item), {
          icon: undefined
        }),
        key: i,
        NavLink: NavLink
      });
    });
  }, [processedPrimaryItems, NavLink]);
  return React.createElement(React.Fragment, null, React.createElement("header", {
    className: "mobile-header bg-white static-top"
  }, React.createElement("div", {
    className: "container d-flex"
  }, React.createElement("div", {
    className: "brand split-brand"
  }, primarySignet, secondarySignet && React.createElement("div", {
    className: "split"
  }), secondarySignet), mobileTopSlot)), React.createElement("nav", {
    className: "navbar navbar-expand-lg navbar-dark navbar-global bg-primary static-top "
  }, React.createElement("div", {
    className: "container"
  }, primaryLogo !== null && primaryLogo !== void 0 ? primaryLogo : React.createElement("a", {
    className: "navbar-brand white-brand",
    href: "#"
  }, React.createElement(Logo, {
    size: 30,
    variant: "logo-mono"
  })), React.createElement("div", {
    className: "collapse navbar-collapse",
    id: "navbarDisappear"
  }, React.createElement("ul", {
    className: "navbar-nav ml-auto"
  }, primaryItemsComponent), React.createElement("ul", {
    className: "navbar-icon-row "
  }, primaryActionItems.map(function (item, i) {
    return React.createElement(ActionItem, {
      item: item,
      key: i,
      NavLink: NavLink
    });
  })), primarySlot))), React.createElement("nav", {
    className: "navbar navbar-expand-lg navbar-light bg-white sticky-top"
  }, React.createElement("div", {
    className: "container"
  }, secondaryLogo, React.createElement("div", {
    className: "mobile-nav-container"
  }, React.createElement("div", {
    className: "navbar-mobile"
  }, React.createElement("ul", {
    className: "navbar-mobile-nav light-icon"
  }, secondaryNavItems.map(function (item, i) {
    return item.icon ? React.createElement(ItemComponent, {
      item: __assign(__assign({}, item), {
        title: undefined
      }),
      key: i,
      NavLink: NavLink
    }) : null;
  }))), React.createElement("button", {
    className: "navbar-toggler light-icon collapsed",
    type: "button",
    id: "navbarToggler",
    onClick: function () {
      return setExpanded(!expanded);
    },
    "aria-controls": "navbarResponsive",
    "aria-expanded": expanded,
    "aria-label": "Toggle navigation"
  }, expanded ? React.createElement(CloseIcon, {
    className: "navbar-toggler-icon"
  }) : React.createElement(MenuIcon, {
    className: "navbar-toggler-icon"
  }))), React.createElement("div", {
    className: "collapse navbar-collapse",
    id: "navbarResponsive_"
  }, React.createElement("ul", {
    className: "navbar-nav ml-auto"
  }, secondaryNavItems.map(function (item, i) {
    return React.createElement(ItemComponent, {
      key: i,
      item: __assign(__assign({}, item), {
        icon: undefined
      }),
      NavLink: NavLink
    });
  })), secondarySlot))), React.createElement("div", {
    className: classNames("collapse nav-drawer", {
      show: expanded
    }),
    id: "navbarResponsive"
  }, React.createElement("div", {
    className: "navbar-collapse"
  }, React.createElement("ul", {
    className: "navbar-nav"
  }, secondaryNavItems.map(function (item, i) {
    return React.createElement(ItemComponent, {
      key: i,
      item: __assign(__assign({}, item), {
        icon: undefined
      }),
      NavLink: NavLink
    });
  }))), React.createElement("div", {
    className: "absolute-bottom"
  }, React.createElement("ul", {
    className: "navbar-icon-row "
  }, primaryActionItems.map(function (item, i) {
    return React.createElement(ActionItem, {
      key: i,
      item: item,
      NavLink: NavLink
    });
  })), drawerSlot)), React.createElement("div", {
    style: {
      height: 40
    }
  }));
};